import { useEffect, useState } from 'react';
import { AgentData } from '../components/AgentModal';
import { RemoteAgentMetadata, RemoteAgentMetrics } from './useAgentMetadata';
import { supervisorBase } from '../utils/georgeUrl';

/**
 * Agents whose KPI tiles are driven by real usage metrics. Only the two
 * agents currently in production (INDUS) qualify; the others (POC) keep the
 * hardcoded KPIs from their metadata / static fallback.
 */
const METRICS_AGENTS = new Set(['servicing', 'retail']);

function metricsToKpis(m: RemoteAgentMetrics): { label: string; value: string }[] {
  return [
    { label: 'Users', value: m.users.toLocaleString() },
    { label: 'Conversations', value: m.conversations.toLocaleString() },
    { label: 'Messages', value: m.questions_processed.toLocaleString() },
  ];
}

function mergeWithRemote(agent: AgentData, remote: RemoteAgentMetadata): AgentData {
  return {
    ...agent,
    title: remote.title,
    description: remote.description,
    fullDescription: remote.full_description,
    tags: remote.tags,
    status: remote.status,
    kpis: remote.kpis ?? agent.kpis,
    coreCapabilities: remote.core_capabilities,
    faqs: remote.faqs,
    updates: remote.updates,
    knowledgeSources: remote.knowledge_sources ?? agent.knowledgeSources,
    owners: {
      agentOwner: remote.owners.agent_owner,
      businessOwner: remote.owners.business_owner,
      supportContact: remote.owners.support_contact,
      version: remote.version,
    },
  };
}

type AggregatedMetadataResponse = Record<string, RemoteAgentMetadata | null>;
type AggregatedMetricsResponse = Record<string, RemoteAgentMetrics>;

export function useAgents(
  staticAgents: AgentData[],
  accessToken: string,
  idToken: string
): AgentData[] {
  const [agents, setAgents] = useState<AgentData[]>(staticAgents);

  useEffect(() => {
    if (!accessToken) return;
    const liveAgents = staticAgents.filter((a) => a.liveMetadata);
    if (!liveAgents.length) return;

    // Both headers are required: the supervisor verifies identity on the ID
    // token (`X-Id-Token`) — the access token from the org AS carries no
    // `groups` claim to authorize against — and still forwards the bearer
    // token downstream to the sub-agents it aggregates.
    const authHeaders = { Authorization: `Bearer ${accessToken}`, 'X-Id-Token': idToken };

    const url = `${supervisorBase()}/api/v1/metadata`;
    console.info('[AgentStore] Fetching aggregated metadata →', url);

    fetch(url, {
      headers: authHeaders,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<AggregatedMetadataResponse>;
      })
      .then((payload) => {
        setAgents((prev) =>
          prev.map((a) => {
            if (!a.liveMetadata) return a;
            const remote = payload[a.id];
            if (remote) {
              console.info(`[AgentStore] ✓ Metadata loaded for "${a.id}" (v${remote.version})`);
              return mergeWithRemote(a, remote);
            }
            console.info(`[AgentStore] ✗ No metadata for "${a.id}", using static fallback`);
            return { ...a, metadataError: true };
          })
        );
      })
      .catch((err: Error) => {
        console.info(
          `[AgentStore] ✗ Aggregated metadata fetch failed (${err.message}), using static fallback`
        );
        setAgents((prev) => prev.map((a) => (a.liveMetadata ? { ...a, metadataError: true } : a)));
      });

    const metricsUrl = `${supervisorBase()}/api/v1/metrics`;
    console.info('[AgentStore] Fetching usage metrics →', metricsUrl);

    fetch(metricsUrl, {
      headers: authHeaders,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<AggregatedMetricsResponse>;
      })
      .then((payload) => {
        setAgents((prev) =>
          prev.map((a) => {
            const metrics = payload[a.id];
            if (!a.liveMetadata || !METRICS_AGENTS.has(a.id) || !metrics) return a;
            return { ...a, kpis: metricsToKpis(metrics) };
          })
        );
      })
      .catch((err: Error) => {
        // Non-fatal: keep whatever KPIs metadata provided (or the static fallback).
        console.info(`[AgentStore] ✗ Usage metrics fetch failed (${err.message}), keeping KPIs`);
      });
  }, [accessToken, idToken]);

  return agents;
}
